import React, { useContext } from 'react';
import { Money } from '../../../components';
import { SettingsContext } from '../../../contexts';
import { getBaseCurrency, formatMoney } from '../../../core/currency';
import { useFx } from '../../../hooks/fx';
import FxUnavailable from '../../../components/formatters/FxUnavailable';

const PulseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M1 8h2.2l1.4-4.2L6.5 11 8.8 4.8 10.2 8H15"
      stroke="#7145D0"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Anything that is not a finite number means the net could not be resolved —
// calculateNetPay returns 'N/A' (string) when a quote is missing and ['N/A'] (an
// ARRAY) when the deal has no sell requests, and it is simply undefined until the
// first calculation lands. Testing only for `string` let the array and undefined
// cases fall through to the store name, so the banner read "Left to pay: by
// <store>" with no figure at all — as if the store owed nothing.
const isResolved = (netPay) => typeof netPay === 'number' && Number.isFinite(netPay);

const payeeLabel = (netPay, isMerchant, request, storeName) => {
  if (!isResolved(netPay)) {
    return 'N/A';
  }

  if (netPay === 0) {
    return 'Nobody';
  }

  return netPay > 0
    ? 'Client'
    : isMerchant
      ? request?.merchant?.merchant_store?.store_name
      : storeName;
};

export function PxCalculation({
  request,
  netPay,
  profitValue,
  profitErr,
  // Currency netPay is computed in (useRequestCalculation.dealCurrency);
  // falls back to the store base when the caller doesn't thread it.
  dealCurrency,
}) {
  const { isMerchant, settings } = useContext(SettingsContext);
  const baseCcy = getBaseCurrency();
  const netPayCurrency = dealCurrency ?? baseCcy;
  // Profit estimate is the GBP pivot — display it in the store's own base
  // currency (×1 / byte-identical for a GBP base or with the flag off).
  // null = no known GBP -> base rate: the estimate is then not shown (never
  // converted 1:1). See FX RULE in hooks/fx.js.
  const { rate: gbpToBase, unavailable: gbpToBaseUnavailable } = useFx('gbp', baseCcy);

  return (
    <header className="calculation-container px-calc-banner">
      <div className="px-calc-banner__main">
        <div className="px-calc-banner__brand">
          <PulseIcon />
          <span className="title">Live Calculation</span>
        </div>

        <div className="px-calc-banner__stat">
          <span className="px-calc-banner__label">Left to pay:</span>
          <span className="px-calc-banner__value">
            {isResolved(netPay) ? (
              <Money
                amount={parseFloat(Math.abs(netPay))}
                currency={netPayCurrency}
                className="money"
                // Money renders a bare &nbsp; for 0 unless given this, which
                // left an even deal reading "Left to pay:  by Nobody". A net of
                // zero is a real answer — show it.
                zero={formatMoney(0, { currency: netPayCurrency, decimals: 0 })}
              />
            ) : (
              typeof netPay === 'string' ? netPay : 'N/A'
            )}
            {' '}by {payeeLabel(netPay, isMerchant, request, settings?.name)}
          </span>
        </div>

        <div className="px-calc-banner__stat">
          <span className="px-calc-banner__label">Profit estimate:</span>
          <span className="px-calc-banner__value">
            {typeof profitValue === 'string' && profitValue}
            {typeof profitValue === 'object' && profitValue && gbpToBase == null && (
              <FxUnavailable unavailable={gbpToBaseUnavailable} />
            )}
            {typeof profitValue === 'object' && profitValue && gbpToBase != null && (
              <>
                <Money
                  amount={profitValue.min * gbpToBase}
                  currency={baseCcy}
                  className="money"
                />
                {' '}-{' '}
                <Money
                  amount={profitValue.max * gbpToBase}
                  currency={baseCcy}
                  className="money"
                />
              </>
            )}
            {profitErr && <span className="profit-error">{profitErr}</span>}
          </span>
        </div>
      </div>
    </header>
  );
}
